import * as React from "react";
import { inject } from "mobx-react";
import { decorateDestinationHeroHtml, Sources } from "../DestinationHeroUtilities";
import { WizardHeroProps } from "../typings";
import { Media } from "typings/microserviceModels/destination-hero-flex-module";
import { useLocalization } from "@shared-ui/localization-context";
import { optimizeHeroImageUrl } from "components/flexComponents/DestinationHero/WizardHeroUtils";
import { getFmId } from "src/stores/ExperienceTemplateStore";
import { BlossomImage } from "components/shared/BlossomImage/BlossomImage";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkText } from "uitk-react-text";

/**
 * This component is capable of handling the hero image in two different ways depending on the value of model.withAltAttribute flag
 *  1) renders plain <div> element of certain dimensions while leveraging its backgroundImage CSS property to set the hero image when
 *     model.withAltAttribute is false or undefined [default]
 *  2) renders <div> element along with <img> element while using the <div> as a parent container for <img> to set its size and <img>
 *     to set the hero image itself along with its text description when model.withAltAttribute is true
 *  All this was done in order to eliminate the gap in SEO between HCOM Classic and HOB landing pages by making hero image have the
 *  text description set via alt attribute. The reason why we keep both implementations and the "plain <div> with backgroundImage" approach
 *  is used by default instead of eliminating its use in favour of using <img>s everywhere is because they aren't fully interchangeable
 *  due to different positioning of html elements. In the default implementation, there's a single <div> element with static position while
 *  we have an absolutely-positioned <img> in the alternative implementation, so that makes them behave differently when WizardHero is used
 *  with any other non-statically positioned element within the same container. Updating the WizardHero for some template without checking
 *  to see if there's any other absolutely-positioned element within its parent-container and without verifying the order in which these
 *  components appear in the markup or whether z-indexes are set properly may result in a broken page layout, most likely - WizardHero obscuring
 *  another absolutely-positioned component (like SearchForm or DatePicker). So, to avoid risks of breaking the current layout of some pages,
 *  the alternative WizardHero implementation can be applied to different templates individually by setting withAltAttribute=true in flex-manager.
 * see: https://jira.expedia.biz/browse/CLDL-61
 * @param props
 */
export const WizardHero = inject("wizardState")((props: WizardHeroProps) => {
  const { templateComponent, model, context, wizardState } = props;

  if (!templateComponent) {
    return null;
  }

  const {
    metadata: { id },
    config: { fmTitleId },
  } = templateComponent;
  const fmId = getFmId(templateComponent);
  const { content, showBackgroundOverlay = true, view, withAltAttribute } = model;
  const { formatText } = useLocalization();

  const firstImage = content && content.media && content.media[0];
  const fallbackImage: Media = {
    // Empty fields are required to adhere to our autogenerated typing for Media.
    cardIdentifier: "",
    mediaId: "",
    mediaUrl: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaUrlTrimmed: "",
    mediaUrlOriginal: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaAlt: "Default HTG hotel image",
    origin: "Expedia",
    source: "",
    author: "",
    authorUrl: "",
    license: { name: "", suite: "", url: "" },
    mediaType: "Image",
  };
  const selectedHeroImage =
    firstImage && !firstImage.mediaUrl.includes("TE_Clouds_card_r1.jpg") ? firstImage : fallbackImage;

  if (wizardState) {
    wizardState.globalWizardState.config.heroImageSrc = selectedHeroImage.mediaUrl;
  }

  const imageSource = optimizeHeroImageUrl(selectedHeroImage.mediaUrl, context?.deviceInformation?.mobile);

  const wizardHeroGradient = showBackgroundOverlay ? "linear-gradient(rgba(0, 0, 0, 0.56), rgba(0, 0, 0, 0.56))" : "";
  const imageSourceAttribute = withAltAttribute ? "" : `url('${imageSource}')`;

  const backgroundImage = `${wizardHeroGradient}${
    wizardHeroGradient && imageSourceAttribute ? ", " : ""
  }${imageSourceAttribute}`;
  const { source } = selectedHeroImage;
  const isTroverSource = source === Sources.Trover;
  const isWikimediaSource = source === Sources.Wikimedia;
  const isAttributionRequired = isTroverSource || isWikimediaSource;
  const attributionClass = isAttributionRequired ? "withAttributionText" : "";
  const searchFormView = view === "search-form-view-vrbo" ? "searchFormView" : "";
  const heroWithAltAttr = withAltAttribute ? "withAltAttr uitk-image" : "";
  const mainClassNames = `DestinationHero wizard ${attributionClass} ${searchFormView} ${heroWithAltAttr}`;

  const heroTitle = (): string => {
    if (!isWikimediaSource) {
      return "";
    }

    return formatText(
      "hero.attribution.wikimediaAlt",
      selectedHeroImage.mediaAlt,
      selectedHeroImage.author,
      selectedHeroImage.license && selectedHeroImage.license.name
    );
  };

  const attributionText = (): React.ReactElement | null => {
    if (!isAttributionRequired) {
      return null;
    }

    const { mediaUrlOriginal, author, authorUrl, license } = selectedHeroImage;
    const licenseSuite = license && license.suite;
    const licenseUrl = license && license.url;

    const htmlToDisplay = () => {
      if (isTroverSource) {
        const troverPhotoAnchor = formatText("hero.attribution.troverLink", author, mediaUrlOriginal);

        return decorateDestinationHeroHtml(troverPhotoAnchor);
      }
      const wikimediaPhotoAnchor = formatText(
        "hero.attribution.wikimediaLink",
        mediaUrlOriginal,
        authorUrl,
        author,
        licenseUrl,
        licenseSuite
      );

      return decorateDestinationHeroHtml(wikimediaPhotoAnchor);
    };

    return (
      <UitkSpacing padding={{ blockstart: "one" }}>
        <UitkText size={200}>
          <UitkSpacing padding={{ block: "two", inline: "three" }}>
            <span dangerouslySetInnerHTML={htmlToDisplay()} />
          </UitkSpacing>
        </UitkText>
      </UitkSpacing>
    );
  };

  return (
    <>
      <div
        className={mainClassNames}
        id={id}
        data-fm={fmId}
        data-fm-title-id={fmTitleId}
        style={!withAltAttribute ? { backgroundImage } : undefined}
        title={heroTitle()}
      >
        {withAltAttribute ? (
          <>
            <BlossomImage
              alt={selectedHeroImage.mediaAlt}
              src={imageSource}
              additionalProps={{ "aria-hidden": "true" }}
            />
            {backgroundImage ? <div className="uitk-image-media" style={{ backgroundImage }} /> : null}
          </>
        ) : null}
      </div>
      {attributionText()}
    </>
  );
});
